/**
 * Classification — PMS securities-master taxonomy (Inc 2b view + Inc 3b editor).
 *
 * The "lens model" (design decisions/2026-07-20_securities-master-classification-design.md):
 * independent DIMENSIONS (lenses), each with its own depth (TIERS) and VALUES.
 *
 *  - Read mode: an accordion — expand a dimension to see its value tree.
 *  - Edit mode (Inc 3b-i): a staged table for the DIMENSION list — add / rename /
 *    reorder / delete dimensions, toggle universal↔scoped, and pick a scoped
 *    dimension's applicability (which Asset-Class values it applies within).
 *    Follows the app-wide Read/Edit transaction protocol (§3g/§3h): edits are
 *    PENDING until "Save changes" commits the batch; Discard reverts; the shared
 *    dirty-form guard gates navigation. Direct write + audit (gated
 *    classification.manage). Tier + value editing arrives in Inc 3b-ii.
 *
 * Access: admin-only page gate; the read API is open to any authenticated user.
 */
import { useMemo, useState } from 'react';
import {
  Accordion, ActionIcon, Alert, Badge, Box, Button, Container, Group, Loader, Modal,
  MultiSelect, Select, Stack, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArrowBackUp, IconGripVertical, IconPencil, IconPlus,
  IconSitemap, IconTrash,
} from '@tabler/icons-react';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  classificationApi,
  type ClassificationDimension,
  type ClassificationValue,
  type DimensionScope,
} from '../api/classification';
import { BackToCompliance } from '../components/BackToCompliance';
import { ClassificationViewsPanel } from '../components/ClassificationViewsPanel';
import { AccessDeniedAlert } from '../components/RequireAccess';
import { useCurrentUser } from '../hooks/useAuth';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const DIMENSIONS_KEY = ['classification', 'dimensions'] as const;
const CODE_RE = /^[a-z0-9_]+$/;
const errMsg = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.');
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

function reorder<T>(arr: T[], from: number, to: number): T[] {
  const copy = arr.slice();
  const [moved] = copy.splice(from, 1);
  copy.splice(to, 0, moved);
  return copy;
}

function ScopeBadge({ dim }: { dim: ClassificationDimension }) {
  if (dim.scope === 'universal') return <Badge color="azul" variant="light">Universal</Badge>;
  return (
    <Badge color="laranja" variant="light">
      Scoped · {dim.applies_to.length ? dim.applies_to.join(', ') : '—'}
    </Badge>
  );
}

/** Read-mode value tree for one dimension, built from the flat (parent_id) list. */
function DimensionValues({ dim }: { dim: ClassificationDimension }) {
  const q = useQuery({
    queryKey: ['classification', 'values', dim.code],
    queryFn: () => classificationApi.values(dim.code),
    staleTime: 60_000,
  });
  const tierNames = dim.tiers.map((t) => t.name).join(' › ');
  if (q.isLoading) return <Loader size="sm" />;
  if (q.isError) return <Alert color="red">{errMsg(q.error)}</Alert>;

  const values = q.data ?? [];
  const byParent = new Map<string | null, ClassificationValue[]>();
  for (const v of values) {
    if (!byParent.has(v.parent_id)) byParent.set(v.parent_id, []);
    byParent.get(v.parent_id)!.push(v);
  }
  const renderLevel = (parentId: string | null, depth: number) => {
    const children = byParent.get(parentId) ?? [];
    if (!children.length) return null;
    return (
      <Stack gap={2}>
        {children.map((v) => (
          <Box key={v.id}>
            <Text size="sm" style={{ paddingLeft: depth * 18 }}>{depth > 0 ? '• ' : ''}{v.label}</Text>
            {renderLevel(v.id, depth + 1)}
          </Box>
        ))}
      </Stack>
    );
  };
  const populated = new Set(values.map((v) => v.tier_level));
  const deferred = dim.tiers.filter((t) => !populated.has(t.tier_level));
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">Levels: {tierNames || '—'} · {values.length} value{values.length === 1 ? '' : 's'}</Text>
      {values.length ? renderLevel(null, 0) : <Text size="sm" c="dimmed">No values yet.</Text>}
      {deferred.length > 0 && values.length > 0 && (
        <Text size="xs" c="dimmed" fs="italic">
          Deeper levels defined, values loaded later: {deferred.map((t) => t.name).join(', ')}.
        </Text>
      )}
    </Stack>
  );
}

/** Immediate (Pattern B) per-dimension tier + value editor. Each add / rename /
 *  reparent / remove commits on the spot with a toast; server guards (in-use,
 *  parent-tier, duplicate) surface as the toast message. */
function ValueEditor({ dim, onDone }: { dim: ClassificationDimension; onDone: () => void }) {
  const qc = useQueryClient();
  const vq = useQuery({
    queryKey: ['classification', 'values', dim.code],
    queryFn: () => classificationApi.values(dim.code),
    staleTime: 60_000,
  });
  const [busy, setBusy] = useState(false);
  const [newTier, setNewTier] = useState('1');
  const [newLabel, setNewLabel] = useState('');
  const [newParent, setNewParent] = useState<string | null>(null);
  const [newTierName, setNewTierName] = useState('');

  const values = vq.data ?? [];
  const tiers = [...dim.tiers].sort((a, b) => a.tier_level - b.tier_level);
  const maxTier = tiers.length ? tiers[tiers.length - 1].tier_level : 0;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['classification', 'values', dim.code] });
    await qc.invalidateQueries({ queryKey: DIMENSIONS_KEY });
  };
  const run = async (fn: () => Promise<unknown>, okMsg?: string) => {
    setBusy(true);
    try { await fn(); await refresh(); if (okMsg) notifications.show({ color: 'green', message: okMsg }); }
    catch (e) { notifications.show({ color: 'red', autoClose: false, message: (e as ApiError).detail ?? errMsg(e) }); }
    finally { setBusy(false); }
  };

  const parentOpts = (tier: number) =>
    values.filter((v) => v.tier_level === tier - 1).map((v) => ({ value: v.id, label: v.label }));

  const addValue = () => {
    const tl = Number(newTier);
    if (!newLabel.trim()) { notifications.show({ color: 'red', message: 'Value needs a label.' }); return; }
    if (tl > 1 && !newParent) { notifications.show({ color: 'red', message: 'Pick a parent (one tier up).' }); return; }
    run(() => classificationApi.createValue(dim.code, {
      tier_level: tl, label: newLabel.trim(), parent_id: tl > 1 ? newParent : null,
    }), 'Value added').then(() => { setNewLabel(''); setNewParent(null); });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600}>Edit "{dim.name}" — tiers &amp; values</Text>
        <Button size="xs" variant="default" onClick={onDone}>Done</Button>
      </Group>
      <Text size="xs" c="dimmed">Changes here apply immediately — each add / rename / remove is saved on the spot.</Text>

      <Stack gap={4}>
        <Text size="sm" fw={500}>Tiers (levels)</Text>
        {tiers.map((t) => (
          <Group key={t.tier_level} gap="xs" wrap="nowrap">
            <Text size="xs" c="dimmed" w={28}>L{t.tier_level}</Text>
            <TextInput size="xs" style={{ flex: 1 }} defaultValue={t.name}
              onBlur={(e) => { const v = e.currentTarget.value.trim(); if (v && v !== t.name) run(() => classificationApi.renameTier(dim.code, t.tier_level, v), 'Tier renamed'); }} />
            {t.tier_level === maxTier && maxTier > 1 && (
              <Tooltip label="Remove deepest tier" withArrow>
                <ActionIcon variant="subtle" color="red" disabled={busy}
                  onClick={() => run(() => classificationApi.deleteTier(dim.code, t.tier_level), 'Tier removed')} aria-label="Remove tier">
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        ))}
        <Group gap="xs">
          <TextInput size="xs" placeholder="New tier name" value={newTierName} onChange={(e) => setNewTierName(e.currentTarget.value)} />
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} disabled={busy || !newTierName.trim()}
            onClick={() => run(() => classificationApi.addTier(dim.code, newTierName.trim()), 'Tier added').then(() => setNewTierName(''))}>
            Add tier
          </Button>
        </Group>
      </Stack>

      <Stack gap={4}>
        <Text size="sm" fw={500}>Values</Text>
        {vq.isLoading && <Loader size="sm" />}
        {values.length === 0 && !vq.isLoading && <Text size="sm" c="dimmed">No values yet.</Text>}
        {values.length > 0 && (
          <Table withTableBorder verticalSpacing={4}>
            <Table.Thead><Table.Tr>
              <Table.Th w={50}>Tier</Table.Th><Table.Th>Label</Table.Th><Table.Th w={220}>Parent</Table.Th><Table.Th w={50} />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {values.map((v) => (
                <Table.Tr key={v.id}>
                  <Table.Td><Text size="xs" c="dimmed">L{v.tier_level}</Text></Table.Td>
                  <Table.Td>
                    <TextInput size="xs" defaultValue={v.label} style={{ paddingLeft: (v.tier_level - 1) * 14 }}
                      onBlur={(e) => { const nv = e.currentTarget.value.trim(); if (nv && nv !== v.label) run(() => classificationApi.updateValue(v.id, { label: nv }), 'Value renamed'); }} />
                  </Table.Td>
                  <Table.Td>
                    {v.tier_level > 1
                      ? <Select size="xs" data={parentOpts(v.tier_level)} value={v.parent_id} allowDeselect={false} searchable
                          onChange={(pid) => { if (pid && pid !== v.parent_id) run(() => classificationApi.updateValue(v.id, { parent_id: pid }), 'Reparented'); }} />
                      : <Text size="xs" c="dimmed">—</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label="Remove" withArrow>
                      <ActionIcon variant="subtle" color="red" disabled={busy}
                        onClick={() => run(() => classificationApi.deleteValue(v.id), 'Value removed')} aria-label="Remove value">
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        <Group gap="xs" align="flex-end" mt={4}>
          <Select size="xs" label="Tier" w={80} data={tiers.map((t) => String(t.tier_level))} value={newTier} allowDeselect={false}
            onChange={(v) => { setNewTier(v ?? '1'); setNewParent(null); }} />
          <TextInput size="xs" label="New value" style={{ flex: 1 }} value={newLabel} onChange={(e) => setNewLabel(e.currentTarget.value)} />
          {Number(newTier) > 1 && (
            <Select size="xs" label="Parent" w={200} data={parentOpts(Number(newTier))} value={newParent} searchable
              placeholder="one tier up" onChange={setNewParent} />
          )}
          <Button size="xs" leftSection={<IconPlus size={14} />} disabled={busy} onClick={addValue}>Add value</Button>
        </Group>
      </Stack>
    </Stack>
  );
}

interface DimDraft {
  rowKey: string; code: string; name: string; scope: DimensionScope;
  sort_order: number; applies_ids: string[];
  origin: 'existing' | 'new'; deleted: boolean;
  origName: string; origScope: DimensionScope; origSort: number; origAppliesIds: string[];
}

const WARNING =
  'Changing the classification taxonomy affects how every security is (and will be) ' +
  'classified and filtered across the securities master. Existing classifications are ' +
  'not rewritten; only new tagging and reports reflect the change.';

export function ClassificationPage() {
  useDocumentTitle('Classification');
  const { user } = useCurrentUser();
  const qc = useQueryClient();

  const q = useQuery({ queryKey: DIMENSIONS_KEY, queryFn: classificationApi.dimensions, staleTime: 60_000 });
  // Asset-Class L1 values drive the scope-applicability picker + label↔id mapping.
  const acQ = useQuery({
    queryKey: ['classification', 'values', 'asset_class'],
    queryFn: () => classificationApi.values('asset_class'),
    staleTime: 60_000,
  });

  const [open, setOpen] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<DimDraft[]>([]);
  const [seq, setSeq] = useState(0);
  const [saving, setSaving] = useState(false);
  const [warn, setWarn] = useState(false);

  const acL1 = useMemo(() => (acQ.data ?? []).filter((v) => v.tier_level === 1), [acQ.data]);
  const acLabelToId = useMemo(() => new Map(acL1.map((v) => [v.label, v.id])), [acL1]);
  const acOptions = useMemo(() => acL1.map((v) => ({ value: v.id, label: v.label })), [acL1]);

  const toDraft = (dims: ClassificationDimension[]): DimDraft[] =>
    dims.map((d) => {
      const applies = d.applies_to.map((lbl) => acLabelToId.get(lbl)).filter((x): x is string => !!x);
      return {
        rowKey: d.code, code: d.code, name: d.name, scope: (d.scope as DimensionScope),
        sort_order: d.sort_order, applies_ids: applies,
        origin: 'existing', deleted: false,
        origName: d.name, origScope: (d.scope as DimensionScope), origSort: d.sort_order, origAppliesIds: applies,
      };
    });

  const dirty = useMemo(() => editMode && draft.some((r) =>
    (r.origin === 'new' && !r.deleted) ||
    (r.origin === 'existing' && r.deleted) ||
    (r.origin === 'existing' && !r.deleted && (
      r.name !== r.origName || r.scope !== r.origScope || r.sort_order !== r.origSort ||
      !sameSet(r.applies_ids, r.origAppliesIds)
    ))
  ), [editMode, draft]);
  const confirmDiscard = useDirtyFormGuard(dirty);

  const enter = () => { setDraft(toDraft(q.data ?? [])); setEditMode(true); };
  const leave = () => { setEditMode(false); setDraft([]); };
  const patch = (k: string, p: Partial<DimDraft>) => setDraft((d) => d.map((r) => (r.rowKey === k ? { ...r, ...p } : r)));
  const addRow = () => {
    const next = draft.length + 1;
    setSeq((n) => n + 1);
    setDraft((d) => [...d, {
      rowKey: `new-${seq}`, code: '', name: '', scope: 'universal', sort_order: next, applies_ids: [],
      origin: 'new', deleted: false, origName: '', origScope: 'universal', origSort: next, origAppliesIds: [],
    }]);
  };
  const removeRow = (r: DimDraft) =>
    r.origin === 'new' ? setDraft((d) => d.filter((x) => x.rowKey !== r.rowKey)) : patch(r.rowKey, { deleted: true });
  const onDragEnd = (res: DropResult) => {
    if (!res.destination) return;
    setDraft((d) => reorder(d, res.source.index, res.destination!.index).map((r, i) => ({ ...r, sort_order: i + 1 })));
  };

  function validate(): string | null {
    const live = draft.filter((r) => !r.deleted);
    for (const r of live) {
      if (r.origin === 'new' && !CODE_RE.test(r.code)) return `New code "${r.code || '(empty)'}" must be lowercase / digits / underscore.`;
      if (r.name.trim().length === 0) return `Dimension "${r.code}" needs a name.`;
    }
    const codes = live.map((r) => r.code);
    const dup = codes.find((c, i) => codes.indexOf(c) !== i);
    if (dup) return `Duplicate dimension code "${dup}".`;
    return null;
  }

  async function save() {
    setWarn(false); setSaving(true);
    const fail: string[] = [];
    try {
      for (const r of draft.filter((x) => x.origin === 'existing' && x.deleted))
        try { await classificationApi.deleteDimension(r.code); } catch (e) { fail.push(`Remove ${r.code}: ${(e as ApiError).detail}`); }
      for (const r of draft.filter((x) => x.origin === 'new' && !x.deleted)) {
        try {
          await classificationApi.createDimension({ code: r.code, name: r.name.trim(), scope: r.scope, sort_order: r.sort_order });
          if (r.scope === 'scoped' && r.applies_ids.length) await classificationApi.setScope(r.code, r.applies_ids);
        } catch (e) { fail.push(`Add ${r.code}: ${(e as ApiError).detail}`); }
      }
      for (const r of draft.filter((x) => x.origin === 'existing' && !x.deleted)) {
        const metaChanged = r.name !== r.origName || r.scope !== r.origScope || r.sort_order !== r.origSort;
        if (metaChanged)
          try { await classificationApi.updateDimension(r.code, { name: r.name.trim(), scope: r.scope, sort_order: r.sort_order }); }
          catch (e) { fail.push(`Update ${r.code}: ${(e as ApiError).detail}`); continue; }
        // Scope applicability: only meaningful for a scoped dim. A switch to universal
        // clears it server-side; a scoped dim with changed membership is re-set.
        if (r.scope === 'scoped' && (!sameSet(r.applies_ids, r.origAppliesIds) || r.scope !== r.origScope))
          try { await classificationApi.setScope(r.code, r.applies_ids); } catch (e) { fail.push(`Scope ${r.code}: ${(e as ApiError).detail}`); }
      }
    } finally { setSaving(false); }
    await qc.invalidateQueries({ queryKey: DIMENSIONS_KEY });
    await qc.invalidateQueries({ queryKey: ['classification', 'values'] });
    if (fail.length === 0) { notifications.show({ color: 'green', message: 'Classification saved' }); leave(); }
    else {
      notifications.show({ color: 'red', title: 'Some changes were not saved', autoClose: false, message: fail.join(' · ') });
      setDraft(toDraft(qc.getQueryData<ClassificationDimension[]>(DIMENSIONS_KEY) ?? []));
    }
  }
  function onSave() {
    const e = validate();
    if (e) { notifications.show({ color: 'red', title: 'Cannot save', message: e }); return; }
    if (!dirty) { leave(); return; }
    setWarn(true);
  }

  if (!user) return null;
  if (user.role !== 'admin') return <AccessDeniedAlert />;

  return (
    <Container size="xl">
      <Stack gap="lg">
        <BackToCompliance to="/admin" label="Back to Admin" />
        <Group justify="space-between">
          <Group gap="xs"><IconSitemap size={22} /><Title order={2}>Classification</Title></Group>
          {!editMode ? (
            <Button leftSection={<IconPencil size={16} />} onClick={enter} disabled={!q.data}>Edit dimensions</Button>
          ) : (
            <Group gap="xs">
              <Button variant="default" leftSection={<IconArrowBackUp size={16} />} onClick={() => confirmDiscard(leave)}>Discard</Button>
              <Button variant="light" leftSection={<IconPlus size={16} />} onClick={addRow}>Add dimension</Button>
              <Button color="azul" loading={saving} onClick={onSave}>Save changes</Button>
            </Group>
          )}
        </Group>
        <Text size="sm" c="dimmed">
          Independent <b>lenses</b> (dimensions), each with its own depth (<b>tiers</b>). A security carries
          one reading per lens. In Edit mode you can add, rename, reorder and remove dimensions and set a
          scoped dimension's applicability; tier &amp; value editing arrives next. Changes are pending until Save.
        </Text>

        {!editMode && <ClassificationViewsPanel />}

        {q.isLoading && <Loader />}
        {q.isError && <Alert color="red">{errMsg(q.error)}</Alert>}

        {q.data && !editMode && (
          <Accordion value={open} onChange={setOpen} variant="separated">
            {q.data.map((d) => (
              <Accordion.Item key={d.code} value={d.code}>
                <Accordion.Control>
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap"><Text fw={600}>{d.name}</Text><ScopeBadge dim={d} /></Group>
                    <Text size="xs" c="dimmed">{d.tiers.length} tier{d.tiers.length === 1 ? '' : 's'}</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {open === d.code && (
                    editingValues === d.code
                      ? <ValueEditor dim={d} onDone={() => setEditingValues(null)} />
                      : (
                        <Stack gap="sm">
                          <DimensionValues dim={d} />
                          <Group>
                            <Button size="xs" variant="light" leftSection={<IconPencil size={14} />}
                              onClick={() => setEditingValues(d.code)}>
                              Edit values &amp; tiers
                            </Button>
                          </Group>
                        </Stack>
                      )
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        )}

        {q.data && editMode && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Table withTableBorder verticalSpacing="xs">
              <Table.Thead><Table.Tr>
                <Table.Th w={40} />
                <Table.Th w={180}>Code</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th w={140}>Scope</Table.Th>
                <Table.Th>Applies to (scoped)</Table.Th>
                <Table.Th w={70}>Order</Table.Th>
                <Table.Th w={60} />
              </Table.Tr></Table.Thead>
              <Droppable droppableId="dimensions">
                {(dp) => (
                  <Table.Tbody ref={dp.innerRef} {...dp.droppableProps}>
                    {draft.map((r, i) => (
                      <Draggable key={r.rowKey} draggableId={r.rowKey} index={i} isDragDisabled={r.deleted}>
                        {(dr) => (
                          <Table.Tr ref={dr.innerRef} {...dr.draggableProps}
                            style={{ ...dr.draggableProps.style, ...(r.deleted ? { opacity: 0.4 } : {}) }}>
                            <Table.Td {...dr.dragHandleProps} style={{ cursor: r.deleted ? 'default' : 'grab' }}>
                              {!r.deleted && <IconGripVertical size={16} color="var(--mantine-color-gray-5)" />}
                            </Table.Td>
                            <Table.Td>
                              {r.origin === 'new'
                                ? <TextInput size="xs" placeholder="code_slug" value={r.code}
                                    onChange={(e) => patch(r.rowKey, { code: e.currentTarget.value.toLowerCase() })} />
                                : <Text ff="monospace" size="sm">{r.code}</Text>}
                            </Table.Td>
                            <Table.Td>
                              {!r.deleted
                                ? <TextInput size="xs" value={r.name} onChange={(e) => patch(r.rowKey, { name: e.currentTarget.value })} />
                                : <Text td="line-through">{r.name}</Text>}
                            </Table.Td>
                            <Table.Td>
                              <Select size="xs" data={[{ value: 'universal', label: 'Universal' }, { value: 'scoped', label: 'Scoped' }]}
                                value={r.scope} allowDeselect={false} disabled={r.deleted}
                                onChange={(v) => patch(r.rowKey, { scope: (v as DimensionScope) ?? 'universal', ...(v === 'universal' ? { applies_ids: [] } : {}) })} />
                            </Table.Td>
                            <Table.Td>
                              {r.scope === 'scoped' && !r.deleted ? (
                                <MultiSelect size="xs" data={acOptions} value={r.applies_ids}
                                  placeholder="Asset classes…" searchable
                                  onChange={(vals) => patch(r.rowKey, { applies_ids: vals })} />
                              ) : <Text size="xs" c="dimmed">—</Text>}
                            </Table.Td>
                            <Table.Td><Text size="sm">{r.sort_order}</Text></Table.Td>
                            <Table.Td>
                              {r.deleted
                                ? <Tooltip label="Undo remove" withArrow><ActionIcon variant="subtle" onClick={() => patch(r.rowKey, { deleted: false })} aria-label="Undo"><IconArrowBackUp size={16} /></ActionIcon></Tooltip>
                                : <Tooltip label="Remove" withArrow><ActionIcon variant="subtle" color="red" onClick={() => removeRow(r)} aria-label="Remove"><IconTrash size={16} /></ActionIcon></Tooltip>}
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </Draggable>
                    ))}
                    {dp.placeholder}
                  </Table.Tbody>
                )}
              </Droppable>
            </Table>
          </DragDropContext>
        )}
      </Stack>

      <Modal opened={warn} onClose={() => setWarn(false)} centered title={<Text fw={600}>Save classification changes</Text>}>
        <Stack>
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="High-impact change">{WARNING}</Alert>
          <Text size="sm">Apply your changes? Removals are blocked if the item is still in use.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setWarn(false)}>Cancel</Button>
            <Button color="azul" loading={saving} onClick={save}>Save changes</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
